import { styled, keyframes } from '@mui/material/styles';
import { Box, Typography, Card, IconButton, Button, Chip } from '@mui/material';

// ============================================================================
// KEYFRAME ANIMATIONS
// ============================================================================

export const glowPulse = keyframes`
  0%, 100% {
    text-shadow: 0 0 10px rgba(255, 255, 255, 0.2);
    filter: brightness(1);
  }
  50% {
    text-shadow: 0 0 20px rgba(255, 255, 255, 0.4);
    filter: brightness(1.2);
  }
`;

export const ambientGlow = keyframes`
  0%, 100% {
    box-shadow: 0 0 20px rgba(0, 212, 255, 0.1), 0 0 40px rgba(0, 212, 255, 0.05);
  }
  50% {
    box-shadow: 0 0 40px rgba(0, 212, 255, 0.2), 0 0 60px rgba(0, 212, 255, 0.1);
  }
`;

export const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-6px); }
`;

export const rotateSpin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

export const energyFlowH = keyframes`
  from { background-position: 0 0; }
  to { background-position: 80px 0; }
`;

export const energyFlowV = keyframes`
  from { background-position: 0 0; }
  to { background-position: 0 80px; }
`;

export const energyPulse = keyframes`
  0%, 100% { opacity: 0.6; filter: brightness(1); }
  50% { opacity: 1; filter: brightness(1.5); }
`;

export const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

export const techHoverPulse = keyframes`
  0%, 100% {
    opacity: 0.4;
    transform: scale(0.92);
  }
  50% {
    opacity: 0.8;
    transform: scale(1.1);
  }
`;

export const hologramBorder = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

// ============================================================================
// TYPOGRAPHY COMPONENTS
// ============================================================================

export const PageTitle = styled(Typography)({
  fontSize: '2rem',
  fontWeight: 700,
  background: 'linear-gradient(135deg, #00d4ff, #ffffff)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  letterSpacing: '0.5px',
  marginBottom: '1.5rem',
  textAlign: 'center',
  animation: `${fadeInUp} 0.6s ease-out`,
});

export const SectionTitle = styled(Typography)({
  fontSize: '1.5rem',
  fontWeight: 600,
  color: '#ffffff',
  letterSpacing: '0.3px',
  marginBottom: '1rem',
  display: 'flex',
  alignItems: 'center',
  gap: '0.75rem',
});

export const CardTitle = styled(Typography)({
  fontSize: '1.1rem',
  fontWeight: 600,
  color: '#ffffff',
  letterSpacing: '0.2px',
});

export const BodyText = styled(Typography)({
  fontSize: '0.95rem',
  color: 'rgba(255, 255, 255, 0.8)',
  lineHeight: 1.6,
  letterSpacing: '0.01em',
});

export const SecondaryText = styled(Typography)({
  fontSize: '0.85rem',
  color: 'rgba(255, 255, 255, 0.6)',
  lineHeight: 1.5,
  letterSpacing: '0.01em',
});

export const CaptionText = styled(Typography)({
  fontSize: '0.75rem',
  color: 'rgba(255, 255, 255, 0.5)',
  lineHeight: 1.4,
  letterSpacing: '0.02em',
  textTransform: 'uppercase',
});

// ============================================================================
// CARD & CONTAINER COMPONENTS
// ============================================================================

export const GlassmorphicCard = styled(Card)(({ theme }) => ({
  background: theme.palette.background.paper,
  backdropFilter: `blur(${theme.custom?.glassBlur || 16}px)`,
  border: '1px solid rgba(255, 255, 255, 0.08)',
  borderRadius: `${theme.custom?.cardRounding || 28}px`,
  boxShadow: '0 20px 40px rgba(0, 0, 0, 0.4)',
  transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
  overflow: 'hidden',
  '&:hover': {
    backgroundImage: 'linear-gradient(rgba(255, 255, 255, 0.03), rgba(255, 255, 255, 0.03))',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.4)',
    transform: 'translateY(-5px)',
  },
}));

export const GlassContainer = styled(Box)(({ theme }) => ({
  borderRadius: `${(theme.custom?.cardRounding || 28) * 1.15}px`,
  backgroundColor: theme.palette.background.paper,
  border: '1px solid rgba(255, 255, 255, 0.05)',
  backdropFilter: `blur(${theme.custom?.glassBlur || 20}px)`,
  boxShadow: '0 40px 80px rgba(0, 0, 0, 0.5)',
  position: 'relative',
  overflow: 'hidden',
}));

export const AmbientGlowBox = styled(Box)(({ color = '#00d4ff' }) => ({
  position: 'relative',
  transition: 'all 0.5s ease',
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    borderRadius: 'inherit',
    boxShadow: `0 0 30px ${color}1a, 0 0 60px ${color}0d`,
    opacity: 0,
    transition: 'opacity 0.5s ease',
    zIndex: -1,
  },
  '&.active::after': {
    opacity: 1,
  },
}));

export const BentoGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
  gap: theme.spacing(3),
  width: '100%',
}));

export const CompactCard = styled(Box)(({ theme }) => ({
  background: 'rgba(255, 255, 255, 0.02)',
  border: '1px solid rgba(255, 255, 255, 0.1)',
  borderRadius: `${(theme.custom?.cardRounding || 28) * 0.6}px`,
  padding: '16px',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    background: 'rgba(0, 212, 255, 0.05)',
    border: `1px solid ${theme.palette.primary.main}33`,
    transform: 'translateY(-2px)',
    boxShadow: `0 8px 24px ${theme.palette.primary.main}1a`,
  },
}));

export const FlexBox = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '0.75rem',
});

export const CenteredBox = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: '200px',
});

// ============================================================================
// BUTTON COMPONENTS
// ============================================================================

export const PrimaryButton = styled(Button)(({ theme }) => ({
  background: theme.palette.secondary.main,
  color: theme.palette.secondary.contrastText,
  fontWeight: 600,
  borderRadius: '12px',
  textTransform: 'none',
  fontSize: '0.95rem',
  padding: '10px 24px',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    background: theme.palette.secondary.light,
    boxShadow: `0 8px 24px ${theme.palette.secondary.main}4d`,
    transform: 'translateY(-2px)',
  },
  '&:disabled': {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
}));

export const SecondaryButton = styled(Button)(({ theme }) => ({
  background: 'transparent',
  color: theme.palette.secondary.main,
  border: `1px solid ${theme.palette.secondary.main}`,
  fontWeight: 600,
  borderRadius: '12px',
  textTransform: 'none',
  fontSize: '0.95rem',
  padding: '10px 24px',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    background: `${theme.palette.secondary.main}1a`,
    border: `1px solid ${theme.palette.secondary.main}`,
    transform: 'translateY(-2px)',
  },
}));

export const ActionIconButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.primary.main,
  background: `${theme.palette.primary.main}1a`,
  border: `1px solid ${theme.palette.primary.main}4d`,
  borderRadius: '12px',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    background: `${theme.palette.primary.main}33`,
    borderColor: theme.palette.primary.main,
    boxShadow: `0 4px 16px ${theme.palette.primary.main}33`,
    transform: 'scale(1.05)',
  },
  '&:disabled': {
    opacity: 0.6,
    color: `${theme.palette.primary.main}80`,
  },
}));

export const DangerButton = styled(Button)(({ theme }) => ({
  background: `${theme.palette.error.main}1a`,
  color: theme.palette.error.main,
  border: `1px solid ${theme.palette.error.main}4d`,
  fontWeight: 600,
  borderRadius: '12px',
  textTransform: 'none',
  fontSize: '0.95rem',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    background: `${theme.palette.error.main}33`,
    border: `1px solid ${theme.palette.error.main}80`,
    transform: 'translateY(-2px)',
  },
}));

export const SuccessButton = styled(Button)(({ theme }) => ({
  background: `${theme.palette.success.main}1a`,
  color: theme.palette.success.main,
  border: `1px solid ${theme.palette.success.main}4d`,
  fontWeight: 600,
  borderRadius: '12px',
  textTransform: 'none',
  fontSize: '0.95rem',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    background: `${theme.palette.success.main}33`,
    border: `1px solid ${theme.palette.success.main}80`,
    transform: 'translateY(-2px)',
  },
}));

// ============================================================================
// STATUS CHIP COMPONENTS
// ============================================================================

export const StatusChipBase = styled(Chip)({
  fontWeight: 600,
  fontSize: '0.75rem',
  height: '24px',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'scale(1.05)',
  },
});

export const getStatusChipStyles = (status) => {
  const statusLower = String(status || 'unknown').toLowerCase();
  
  let bgColor, textColor, borderColor;

  if (statusLower.includes('running') || statusLower.includes('up') || statusLower.includes('active')) {
    bgColor = 'rgba(0, 255, 136, 0.1)';
    textColor = '#00ff88';
    borderColor = 'rgba(0, 255, 136, 0.3)';
  } else if (statusLower.includes('stopped') || statusLower.includes('down') || statusLower.includes('inactive')) {
    bgColor = 'rgba(255, 68, 68, 0.1)';
    textColor = '#ff4444';
    borderColor = 'rgba(255, 68, 68, 0.3)';
  } else if (statusLower.includes('starting') || statusLower.includes('stopping')) {
    bgColor = 'rgba(255, 193, 7, 0.1)';
    textColor = '#ffc107';
    borderColor = 'rgba(255, 193, 7, 0.3)';
  } else {
    bgColor = 'rgba(158, 158, 158, 0.1)';
    textColor = '#9e9e9e';
    borderColor = 'rgba(158, 158, 158, 0.3)';
  }

  return {
    background: bgColor,
    color: textColor,
    border: `1px solid ${borderColor}`,
    fontWeight: 500,
  };
};

// ============================================================================
// ICON COMPONENTS
// ============================================================================

export const IconWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '50%',
  background: `${theme.palette.primary.main}1a`,
  padding: '8px',
}));

export const StatusDot = styled(Box)(({ theme, status }) => ({
  width: '8px',
  height: '8px',
  borderRadius: '50%',
  background: status === 'online' || status === 'running' ? theme.palette.success.main : theme.palette.error.main,
  boxShadow: status === 'online' || status === 'running' 
    ? `0 0 8px ${theme.palette.success.main}80`
    : `0 0 8px ${theme.palette.error.main}80`,
}));

// ============================================================================
// TABLE & LIST COMPONENTS
// ============================================================================

export const StyledTable = styled(Box)(({ theme }) => ({
  background: 'rgba(255, 255, 255, 0.02)',
  borderRadius: `${(theme.custom?.cardRounding || 28) * 0.6}px`,
  backdropFilter: `blur(${(theme.custom?.glassBlur || 20) * 0.5}px)`,
  border: '1px solid rgba(255, 255, 255, 0.06)',
  overflow: 'hidden',
}));

// ============================================================================
// RESPONSIVE UTILITIES
// ============================================================================

export const ResponsiveBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  [theme.breakpoints.up('sm')]: {
    gap: theme.spacing(3),
  },
  [theme.breakpoints.up('md')]: {
    gap: theme.spacing(4),
  },
}));

export const ResponsivePadding = {
  xs: { xs: 2, sm: 3, md: 4 },
  sm: { xs: 1.5, sm: 2, md: 3 },
  lg: { xs: 1, sm: 1.5, md: 2 },
};

export const ResponsiveFontSize = {
  h4: { xs: '1.2rem', sm: '1.5rem', md: '2rem' },
  h5: { xs: '1rem', sm: '1.25rem', md: '1.5rem' },
  h6: { xs: '0.9rem', sm: '1rem', md: '1.1rem' },
  body: { xs: '0.875rem', sm: '0.95rem', md: '1rem' },
  caption: { xs: '0.75rem', sm: '0.85rem', md: '0.95rem' },
};

export default {
  glowPulse,
  float,
  rotateSpin,
  fadeInUp,
  techHoverPulse,
  hologramBorder,
  PageTitle,
  SectionTitle,
  CardTitle,
  BodyText,
  SecondaryText,
  CaptionText,
  GlassmorphicCard,
  CompactCard,
  FlexBox,
  CenteredBox,
  PrimaryButton,
  SecondaryButton,
  ActionIconButton,
  DangerButton,
  SuccessButton,
  StatusChipBase,
  getStatusChipStyles,
  IconWrapper,
  StatusDot,
  StyledTable,
  ResponsiveBox,
  ResponsivePadding,
  ResponsiveFontSize,
};
